import type { ReactNode } from 'react';

import { getStockStatusChecks, type StockInfo } from '../../../../product/index';
import {
  ProductStockTooltip as ProductStockTooltipRoot,
  ProductStockTooltipContent,
  ProductStockLevel,
  ProductStockTooltipTrigger,
} from '@vs-shop/commerce-ui/productStock';
import { getMaxOrderableInf } from '../../../../shared/utils/stockValidation';

import { StockQuantity } from '../../page/product/panel/ProductStockItem/StockQuantity';

type Props = {
  stock: StockInfo;
  children: ReactNode;
};

/**
 * rcprofi's compact availability line: the e-shop's own quantity, with the full
 * block behind it.
 *
 * The level is this tenant's reading of its four stock statuses — anything
 * awaited reads as pending — and the sentence is its own copy. The tooltip and
 * the pointer-swallowing surface are `@vs-shop/commerce-ui`'s.
 */
export default function ProductStockTooltip({ stock, children }: Props) {
  const maxOrderableInf = getMaxOrderableInf(stock.eshop.maxOrderable);
  const eshopDispatchDate = stock.eshop.dispatchDate ?? null;

  const { isInStock, isPreOrder, isSupplierStock } = getStockStatusChecks(stock.status);

  // Exactly one of the four statuses holds, so "held" and "awaited" is the
  // whole of what this line says.
  const level = isInStock ? ProductStockLevel.AVAILABLE : ProductStockLevel.PENDING;

  return (
    <ProductStockTooltipRoot>
      <ProductStockTooltipTrigger variant={level}>
        <StockQuantity
          availableQuantity={maxOrderableInf}
          showStockPrefix={true}
          isPreOrder={isPreOrder}
          isSupplierStock={isSupplierStock}
          dispatchDate={eshopDispatchDate}
        />
      </ProductStockTooltipTrigger>
      <ProductStockTooltipContent>{children}</ProductStockTooltipContent>
    </ProductStockTooltipRoot>
  );
}
